import React, { useEffect, useState } from 'react';
import { createGroup, listGroups, updateGroup, deleteGroup } from '../../api/superadmin';
import './CompanyGroupsSection.css';

const emptyGroupForm = { name: '', address: '' };

// Message du serveur s'il y en a un (ex. "Un group "X" existe deja"), sinon `fallback`.
const serverMessage = (err, fallback) => {
  try {
    const p = JSON.parse(err.message);
    const msg = p?.name?.[0] || p?.detail;
    if (msg) return `${fallback} — ${msg}`;
  } catch (_) { /* noop */ }
  return fallback;
};

const CompanyGroupsSection = ({ companyId }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyGroupForm);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyGroupForm);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    listGroups(companyId)
      .then(setGroups)
      .catch(() => setError('Impossible de charger les groups.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [companyId]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateEditField = (field, value) => setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const created = await createGroup({ company: companyId, ...form });
      setGroups((prev) => [...prev, created]);
      setForm(emptyGroupForm);
    } catch (err) {
      setError(serverMessage(err, 'Creation impossible'));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (group) => {
    setEditId(group.id);
    setEditForm({ name: group.name || '', address: group.address || '', is_print_adresse: !!group.is_print_adresse });
  };

  const saveEdit = async (id) => {
    setBusyId(id);
    setError('');
    try {
      const updated = await updateGroup(id, { name: editForm.name, address: editForm.address, is_print_adresse: !!editForm.is_print_adresse });
      setGroups((prev) => prev.map((g) => (g.id === id ? updated : g)));
      setEditId(null);
    } catch (err) {
      setError(serverMessage(err, 'Modification impossible'));
    } finally {
      setBusyId(null);
    }
  };

  const removeGroup = async (group) => {
    if (!window.confirm(
      `Supprimer le group "${group.name}" ?\n\n`
      + '- Ses banks restent (avec toutes leurs fiches) mais seront sans group.\n'
      + '- Les parametres propres a ce group (loteries, limites, regles Securite) seront effaces.'
    )) return;
    setBusyId(group.id);
    setError('');
    try {
      await deleteGroup(group.id);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
    } catch (err) {
      setError(serverMessage(err, 'Suppression impossible'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="cgs-section">
      <h3 className="cgs-title">Groups</h3>
      <p className="cgs-note">
        Le superadmin cree ici des groups nus (nom + adresse). Le rattachement a un
        superviseur se fait cote company, dans Parametres &gt; Superviseurs.
      </p>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <table className="cgs-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nom</th>
              <th>Superviseur</th>
              <th>Adresse</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              editId === group.id ? (
                <tr key={group.id}>
                  <td>{group.seq}</td>
                  <td>
                    <input className="cgs-input cgs-input-inline" value={editForm.name}
                      onChange={(e) => updateEditField('name', e.target.value)} />
                  </td>
                  <td>{group.supervisor_name || '—'}</td>
                  <td>
                    <input className="cgs-input cgs-input-inline" value={editForm.address}
                      onChange={(e) => updateEditField('address', e.target.value)} />
                    <label className="cgs-print-address">
                      <input type="checkbox" checked={!!editForm.is_print_adresse}
                        onChange={(e) => updateEditField('is_print_adresse', e.target.checked)} />
                      Imprimer sur les tickets
                    </label>
                  </td>
                  <td className="cgs-actions">
                    <button type="button" onClick={() => saveEdit(group.id)} disabled={busyId === group.id}>
                      Enregistrer
                    </button>
                    <button type="button" onClick={() => setEditId(null)}>Annuler</button>
                  </td>
                </tr>
              ) : (
                <tr key={group.id}>
                  <td>{group.seq}</td>
                  <td>{group.display_name || group.name}</td>
                  <td>{group.supervisor_name || '—'}</td>
                  <td>
                    {group.address}
                    {group.is_print_adresse && <span className="cgs-print-badge"> (imprimée sur les tickets)</span>}
                  </td>
                  <td className="cgs-actions">
                    <button type="button" onClick={() => startEdit(group)}>Modifier</button>
                    <button type="button" className="cgs-del" onClick={() => removeGroup(group)}
                      disabled={busyId === group.id}>Supprimer</button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      )}

      <form className="cgs-form" onSubmit={handleCreate}>
        <div className="cgs-row">
          <label className="cgs-label">Nom</label>
          <input
            className="cgs-input"
            type="text"
            placeholder="ex: PIPO"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
          />
        </div>
        <div className="cgs-row">
          <label className="cgs-label">Adresse</label>
          <input
            className="cgs-input"
            type="text"
            value={form.address}
            onChange={(e) => updateField('address', e.target.value)}
          />
        </div>
        {error && <p className="cgs-error">{error}</p>}
        <button type="submit" className="cgs-add-btn" disabled={creating}>
          {creating ? 'Creation...' : '+ Ajouter un group'}
        </button>
      </form>
    </div>
  );
};

export default CompanyGroupsSection;
